"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listNotifications, markNotificationRead } from "@/lib/api/notifications";
import { useCursorList } from "./use-cursor-list";

const NOTIFICATIONS = ["notifications"] as const;

/**
 * FR-68: "all channels deliver within 60 s". The platform raises an in-app
 * notification when the event is evaluated; the console learns of it by
 * polling, so the poll interval is part of that budget and is set well inside
 * it.
 */
export const NOTIFICATION_POLL_MS = 20_000;

/** The caller's first page of notifications and the unread count — the badge. */
export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS,
    queryFn: () => listNotifications(),
    refetchInterval: NOTIFICATION_POLL_MS,
  });
}

/**
 * Every page of the caller's notifications, newest first, for the
 * notifications page. Under the same key prefix as the badge, so marking one
 * read refreshes both.
 */
export function useNotificationList() {
  return useCursorList([...NOTIFICATIONS, "list"] as const, (cursor?: string) => listNotifications(cursor));
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS }),
  });
}
